
type DealType = "bad" | "poor" | "fair" | "good" | "great";
type DealLabelSize = "small" | "medium" | "large";

interface DealLabelProps {
  type: DealType;
  size?: DealLabelSize;
  className?: string;
}

const LABELS: Record<DealType, string> = {
  bad: "Bad Deal",
  poor: "Poor Deal",
  fair: "Fair Deal",
  good: "Good Deal",
  great: "Great Deal",
};

const SVG_PATHS: Record<DealType, { d: string; stroke: string; viewBox: string }> = {
  bad: {
    d: "M2.25 1.87385L16.875 16.4989L27 6.37385L43.875 6.37385V23.2489M43.875 23.2489L35.1246 14.4987M43.875 23.2489L52.6254 14.4987",
    stroke: "#FF2C2C",
    viewBox: "0 0 55 27",
  },
  poor: {
    d: "M1.75 1.75L16.375 16.375L26.5 6.25L43.375 23.125M43.375 23.125H31M43.375 23.125V10.75",
    stroke: "#FF5721",
    viewBox: "0 0 46 26",
  },
  fair: {
    d: "M2.5 11.0001H14H25.25H47.5M47.5 11.0001L38.5 18.875M47.5 11.0001L38.5 2",
    stroke: "#F9DD8D",
    viewBox: "0 0 49 21",
  },
  good: {
    d: "M1.75 24.125L16.375 9.5L26.5 19.625L43.375 2.75M43.375 2.75H31M43.375 2.75V15.125",
    stroke: "#ADEB83",
    viewBox: "0 0 46 26",
  },
  great: {
    d: "M2.25 25.125L16.875 10.5L27 20.625H43.875V3.75M43.875 3.75L52.6254 12.5006M43.875 3.75L34.875 12.5006",
    stroke: "#2E7D32",
    viewBox: "0 0 55 27",
  },
};

const SVG_SIZES: Record<DealLabelSize, { width: number; height: number }> = {
  small: { width: 18, height: 9 },
  medium: { width: 28, height: 13 },
  large: { width: 55, height: 27 },
};

const BORDER_COLORS: Record<DealType, string> = {
  bad: "border-red",
  poor: "border-poor-red",
  fair: "border-yellow",
  good: "border-green",
  great: "border-great-green",
};

const SIZE_STYLES: Record<DealLabelSize, string> = {
  small: "gap-1 px-1",
  medium: "py-2 px-3 gap-2",
  large: "py-[13px] px-3 gap-3",
};

const TEXT_SIZE_STYLES: Record<DealLabelSize, string> = {
  small: "text-xs scale-90",
  medium: "text-sm",
  large: "text-sm md:text-lg",
};

const DealLabel: React.FC<DealLabelProps> = ({ type, size = "large", className = "" }) => {
  const { d, stroke, viewBox } = SVG_PATHS[type];
  const { width, height } = SVG_SIZES[size];

  return (
    <div
      className={`flex items-center font-raleway whitespace-nowrap leading-none border bg-white ${BORDER_COLORS[type]} ${SIZE_STYLES[size]} ${className}`}
    >
      <svg
        width={width}
        height={height}
        viewBox={viewBox}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="block"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d={d}
          stroke={stroke}
          strokeWidth="4"
          strokeLinecap="round"
        />
      </svg>
      <div className={TEXT_SIZE_STYLES[size]}>{LABELS[type]}</div>
    </div>
  );
};

export default DealLabel;
